import axios from 'axios';

// Configure base API URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://your-api-gateway-url.execute-api.us-east-1.amazonaws.com/dev';

// Get token from local storage
const getAuthHeader = () => {
  const token = localStorage.getItem('tradingview_app_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

//emergency throttling
let lastRequestTime = 0;
let lastResponseCache = null;  // Define this here
const THROTTLE_DELAY = 300; // 1 second between requests

// Signal API methods
export const signalsApi = {
  // Get all signals with optional pagination and filtering
  getAll: async (limit = 50, nextToken = null, filters = {}) => {
    
    //ermergency throttling
    const now = Date.now();
    if (now - lastRequestTime < THROTTLE_DELAY) {
      console.log('Request throttled');
      return lastResponseCache || { signals: [], count: 0 };
    }
    lastRequestTime = now;
    
    const { searchTerm, signalType, timeframe, sortField, sortDirection } = filters || {};
    
    let url = `/signals?limit=${limit}`;
    if (nextToken) url += `&nextToken=${encodeURIComponent(nextToken)}`;
    if (searchTerm) url += `&searchTerm=${encodeURIComponent(searchTerm)}`;
    if (signalType && signalType !== 'all') url += `&signalType=${encodeURIComponent(signalType)}`;
    if (timeframe && timeframe !== 'all') url += `&timeframe=${encodeURIComponent(timeframe)}`;
    if (sortField) url += `&sortField=${encodeURIComponent(sortField)}`;
    if (sortDirection) url += `&sortDirection=${encodeURIComponent(sortDirection)}`;
    
    const response = await api.get(url, { headers: getAuthHeader() });
    lastResponseCache = response.data;
    return response.data;
  },
  
  // Get signals only for active trades with filtering
  getActiveTradeSignals: async (filters = {}) => {
    const { searchTerm, signalType, timeframe, sortField, sortDirection } = filters || {};
    
    let url = '/signals/active';
    let queryParams = [];
    
    if (searchTerm) queryParams.push(`searchTerm=${encodeURIComponent(searchTerm)}`);
    if (signalType && signalType !== 'all') queryParams.push(`signalType=${encodeURIComponent(signalType)}`);
    if (timeframe && timeframe !== 'all') queryParams.push(`timeframe=${encodeURIComponent(timeframe)}`);
    if (sortField) queryParams.push(`sortField=${encodeURIComponent(sortField)}`);
    if (sortDirection) queryParams.push(`sortDirection=${encodeURIComponent(sortDirection)}`);
    
    if (queryParams.length > 0) {
      url += '?' + queryParams.join('&');
    }
    
    const response = await api.get(url, { headers: getAuthHeader() });
    return response.data;
  },
  
  // Delete a single signal by ID
  deleteSignal: async (id) => {
    const response = await api.delete(`/signals/${id}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // Delete multiple signals by ID array
  deleteMultipleSignals: async (ids) => {
    const response = await api.post('/signals/batch-delete', 
      { ids },
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  // Delete all signals (optionally with filters)
  deleteAllSignals: async (filters = {}, confirm = false) => {
    const { searchTerm, signalType, timeframe } = filters || {};
    
    let url = '/signals/delete-all?confirm=' + (confirm ? 'true' : 'false');
    
    if (searchTerm) url += `&searchTerm=${encodeURIComponent(searchTerm)}`;
    if (signalType && signalType !== 'all') url += `&signalType=${encodeURIComponent(signalType)}`;
    if (timeframe && timeframe !== 'all') url += `&timeframe=${encodeURIComponent(timeframe)}`;
    
    const response = await api.delete(url, { 
      headers: getAuthHeader() 
    });
    return response.data;
  },
};

// Active trades API methods
export const activeTradesApi = {
  // Get all active trades
  getAll: async () => {
    const response = await api.get('/active-trades', { headers: getAuthHeader() });
    return response.data;
  },
  
  // Add a new active trade
  add: async (symbol, notes = '') => {
    const response = await api.post('/active-trades', { symbol, notes }, { headers: getAuthHeader() });
    return response.data;
  },
  
  // Remove an active trade
  remove: async (symbol) => {
    const response = await api.delete(`/active-trades/${symbol}`, { headers: getAuthHeader() });
    return response.data;
  }
};

// Setups API methods
export const setupsApi = {
  getAll: async () => {
    const response = await api.get('/setups', { headers: getAuthHeader() });
    return response.data;
  }
};

const apiService = {
  signals: signalsApi,
  activeTrades: activeTradesApi,
  setups: setupsApi
};

export default apiService;